import React, { useEffect, useState } from 'react';
import { Box, Button, TextField, Typography, useTheme, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, Alert, Grid, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import PageTitle from 'src/components/PageTitle';
import PageTitleWrapper from 'src/components/PageTitleWrapper';
import { Helmet } from 'react-helmet-async';
import AddOperator from './AddOperator';
import ManageUserRights from './ManageOperatorRights';
import EditOperator from './EditOperator';
import { useNavigate } from 'react-router';
import { authenticationService } from 'src/services/authentication.service';
import { get, deleteEntry, multy_post } from 'src/services/httpClient.service';
import { useSnackbar } from 'src/components/snackbar/useSnackbar';
import ConfirmDeleteDialog from 'src/components/DeleteDialogbox/ConfirmDeleteDialog';
import { getResponsiveStyles, isMobileScreen } from 'src/common/utils/responsive';
import * as XLSX from 'xlsx';
import DownloadIcon from '@mui/icons-material/Download';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

const OperatorScreen = () => {

  const accountType = localStorage.getItem('accountType');
  const theme = useTheme();
  const styles = getResponsiveStyles(theme);
  const [isMobile, setIsMobile] = useState(isMobileScreen(theme));
  const API_URL = process.env.REACT_APP_BASE_API_URL;
  const { vendorId, franchiseId, companyId, operatorId } = authenticationService.getVendorFranchiseIds();
  const { showSnackbar } = useSnackbar();
  const [users, setUsers] = useState<any[]>([]);
  const [distributors, setDistributors] = useState<any[]>([]);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editUserId, setEditUserId] = useState<string | null>(null);
  const [rightsUserId, setRightsUserId] = useState<any | null>(null);
  const [passright, setpassright] = useState<any | null>('right');
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteName, setDeleteName] = useState<string>('');

  // Bulk Upload States
  const [openBulkDialog, setOpenBulkDialog] = useState(false);
  //const [openBulkDistributorDialog, setOpenBulkDistributorDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadErrors, setUploadErrors] = useState<string[]>([]);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);
  const [companiesList, setCompaniesList] = useState<any[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [priceLists, setPriceLists] = useState<any[]>([]);
  const [billingTaxTypes, setBillingTaxTypes] = useState<any[]>([]);
  const [priceListId, setPriceListId] = useState("");
  const [billingTaxId, setBillingTaxId] = useState("");
  const [selectedCompanyName, setSelectedCompanyName] = useState("");

  const [selectedDistributorId, setSelectedDistributorId] = useState("");
  const [selectedUploadType, setSelectedUploadType] = useState("LCO");
  const [uploadFields, setUploadFields] = useState({
    showLcoFields: true,
    showDistributorFields: false,
  });
  const [accountTypeFilter, setAccountTypeFilter] = useState("");

  // Expected headers (exact match for what the backend expects)
  const expectedHeaders = [
    "Country", "State", "District", "City", "Area",
    "LCO/Distributor", "Email", "Password", "Phone", "Mobile",
    "Address", "Postal Code", "Status"
  ];

  // Check if user has permission to upload LCOs (Distributor or LCO)
  // const canUploadLCO = () => {
  //   const accountType = localStorage.getItem('accountType');
  //   return accountType === 'DISTRIBUTOR' || accountType === 'LCO';
  // };

  const getOperatorList = async () => {
    try {
      setLoading(true);
      let response = null;
      if(accountType==="OWNER") {
        response = await get(`${API_URL}/api/operators/vendor/${vendorId}/getOperatorAccountTypeList`);
      } else {
        response = await get(`${API_URL}/api/operators/vendor/${vendorId}/getByDistributorId`, {params: {distributorId: operatorId}});
      }

      setUsers(response?.data || []);
      setDistributors(response?.data.filter(op => { return op.accountType==="DISTRIBUTOR" }) || []);

    } catch (error: any) {
      if (error?.response?.status === 404) {
        setUsers([]);
      } else {
        showSnackbar(
          error?.message || 'Failed to fetch operators',
          'error'
        );
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getOperatorList();
  }, []);

  // Filter Search
  const filteredRows = users?.filter((user) => {
  const matchesSearch =
    [user?.operatorName, user?.email, user?.phone]
      .some((val) =>
        val?.toString().toLowerCase().includes(searchQuery.toLowerCase())
      );

  const matchesAccountType =
    !accountTypeFilter || user?.accountType === accountTypeFilter;

  return matchesSearch && matchesAccountType;
});

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  // ==================== BULK UPLOAD FUNCTIONS ====================

  const handleFile = (file: File) => {
    if (!file) return;

    // Check file type
    const validExtensions = ['.xlsx', '.xls', '.csv'];
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    
    if (!validExtensions.includes(fileExtension)) {
      setUploadErrors(["Only .xlsx, .xls or .csv files are allowed"]);
      setSelectedFile(null);
      return;
    }

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setUploadErrors(["File size exceeds 5MB limit"]);
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);
    setUploadErrors([]);
    setUploadSuccess(null);
  };

  const validateFile = async (file: File): Promise<string[]> => {
    const errors: string[] = [];

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const sheet = workbook.Sheets[workbook.SheetNames[0]];

      const sheetData = XLSX.utils.sheet_to_json(sheet, {
        header: 1,
        defval: ""
      });

      if (!sheetData || sheetData.length === 0) {
        return ["Excel file is empty"];
      }

      const headerRow = sheetData[0];

      if (!Array.isArray(headerRow)) {
        return ["Invalid header row format"];
      }

      // Normalize: trim and convert to uppercase for comparison
      const actualHeaders = headerRow.map(h => 
        String(h).trim().toUpperCase()
      );

      const expectedHeadersUpper = expectedHeaders.map(h => 
        h.toUpperCase()
      );

      // Check if any required headers are missing
      for (let i = 0; i < expectedHeadersUpper.length; i++) {
        if (i < actualHeaders.length) {
          if (actualHeaders[i] !== expectedHeadersUpper[i]) {
            errors.push(
              `Column ${i + 1}: expected "${expectedHeaders[i]}" but found "${headerRow[i] || ''}"`
            );
          }
        } else {
          errors.push(
            `Column ${i + 1}: expected "${expectedHeaders[i]}" but found "MISSING"`
          );
        }
      }

      // Check for extra columns
      if (actualHeaders.length > expectedHeadersUpper.length) {
        const extraHeaders = [];
        for (let i = expectedHeadersUpper.length; i < actualHeaders.length; i++) {
          if (actualHeaders[i] && actualHeaders[i].trim()) {
            extraHeaders.push(actualHeaders[i]);
          }
        }
        if (extraHeaders.length > 0) {
          errors.push(`Extra columns found: ${extraHeaders.join(', ')}. These will be ignored.`);
        }
      }

      // Check for empty rows (skip header)
      if (sheetData.length < 2) {
        errors.push("File has no data rows. Please add at least one LCO entry.");
      }

      

    } catch (error) {
      errors.push("Failed to read file. Please make sure it's a valid Excel file.");
    }

    return errors;
  };

  const handleUpload = async () => {
    if (!selectedFile) {
    setUploadErrors(["No file selected"]);
    return;
  }

  setUploadLoading(true);
  setUploadErrors([]);
  setUploadSuccess(null);

  const errors = await validateFile(selectedFile);

  if (errors.length) {
    setUploadErrors(errors);
    setUploadLoading(false);
    return;
  }

  try {
    const formData = new FormData();

    formData.append("file", selectedFile);
    formData.append("vendorId", vendorId);
    formData.append("franchiseId", franchiseId);

    formData.append("companyId", selectedCompanyId);
    formData.append("companyName", selectedCompanyName);
    formData.append("priceListId", priceListId);
    formData.append("billingTaxId", billingTaxId);
    formData.append("distributorId", selectedDistributorId);

    console.log('formdata',formData)

    if(selectedUploadType === "DISTRIBUTOR") {
      if (!selectedCompanyId) {
        showSnackbar("Please select Company", "error");
        return;
      }

      if (!priceListId) {
        showSnackbar("Please select Franchisee Price List", "error");
        return;
      }

      if (!billingTaxId) {
        showSnackbar("Please select Billing Tax Type", "error");
        return;
      }
    } else {
      if (!selectedDistributorId) {
        showSnackbar("Please select Distributor", "error");
        return;
      }
    }

    if (!selectedFile) {
      showSnackbar("Please select Excel file", "error");
      return;
    }

    let response = null;

    if(selectedUploadType === "LCO") {
      response = await multy_post(`${API_URL}/api/import/lco/upload`, formData);
    } else {
      response = await multy_post(`${API_URL}/api/import/distributor/upload`, formData);
    }

    const responseCode =
      response.responseCode ||
      response.statusCode ||
      response.status;

    if (
      responseCode === 200 ||
      responseCode === "200" ||
      responseCode === 201
    ) {
      showSnackbar(response.message, "success");
      setUploadSuccess(response.message);

      await getOperatorList();

      setTimeout(() => {
        setOpenBulkDialog(false);
        setSelectedFile(null);
        setUploadSuccess(null);
      }, 1500);
    } else {
      setUploadErrors([response.message]);
    }
  } catch (err: any) {
    setUploadErrors([
      err?.response?.data?.message || "LCO/Distributor upload failed"
    ]);
  } finally {
    setUploadLoading(false);
  }
};

  const downloadSampleFile = () => {
    // Create sample data with headers and example rows
    const sampleData = [expectedHeaders];

    const ws = XLSX.utils.aoa_to_sheet(sampleData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "LCO or Distributor Bulk Upload");

    XLSX.writeFile(wb, "lco_or_distributor_bulk_upload_sample.xlsx");
    
    showSnackbar("Sample file downloaded successfully", "success");
  };


  const handleUploadFieldsChange = (type) => {
    setSelectedUploadType(type);

    switch (type) {
      case "LCO":
        setUploadFields({
          showLcoFields: true,
          showDistributorFields: false,
        });
        break;

      case "DISTRIBUTOR":
        setUploadFields({
          showLcoFields: false,
          showDistributorFields: true,
        });
        break;

      default:
        setUploadFields({
          showLcoFields: false,
          showDistributorFields: false,
        });
        break;
    }
  };

  // ==================== COLUMNS ====================

  const columns = [
    { field: 'operatorName', headerName: 'Name', flex: 1 },
    { field: 'email', headerName: 'Email', flex: 1 },
    { field: 'phone', headerName: 'Phone', flex: 1 },
    { field: 'regCode', headerName: 'Regd. Code', flex: 1 },
    { field: 'accountType', headerName: 'Account Type', flex: 1 },
    { field: 'distributorName', headerName: 'Distributor', flex: 1 },
    { 
      field: 'status', 
      headerName: 'Status', 
      flex: 1,
      renderCell: (params: any) => (
        <Box
          sx={{
            backgroundColor: params.value === 'ACTIVE' ? '#4caf50' : '#f44336',
            color: 'white',
            padding: '4px 12px',
            borderRadius: '16px',
            display: 'inline-block',
            fontSize: '12px',
            fontWeight: 'bold'
          }}
        >
          {params.value}
        </Box>
      )
    },
    {
      field: 'edit',
      headerName: 'Edit',
      flex: 0.6,
      renderCell: (params: any) => (
        <Button
          variant="outlined"
          size="small"
          onClick={() => navigate(`/Operator/editoperator/${params.row.id}`)}
        >
          Edit
        </Button>
      ),
    },
    {
      field: 'delete',
      headerName: 'Delete',
      flex: 0.6,
      renderCell: (params: any) => (
        <Button
          variant="outlined"
          color="error"
          size="small"
          onClick={() => {
            setDeleteId(params.row.id);
            setDeleteName(params.row.operatorName);
            setOpenDeleteDialog(true);
          }}
        >
          Delete
        </Button>
      ),
    }
  ];

  const deleteOperator = async () => {
    try {
      setLoading(true);
      await deleteEntry(`${API_URL}/api/operators/${deleteId}`);
      await getOperatorList();
      showSnackbar('Operator deleted successfully', 'success');
    } catch (error) {
      showSnackbar(error?.message || 'Failed to delete operator', 'error');
    } finally {
      setLoading(false);
      setOpenDeleteDialog(false);
      setDeleteId(null);
    }
  };

  /*
  //bulk distributor
  const distributorExpectedHeaders = [
  "Country",
  "State",
  "District",
  "City",
  "Area",
  "Name",
  "Email",
  "Password",
  "Phone",
  "Mobile",
  "Address1",
  "Address2",
  "Address3",
  "Postal Code",
  "Zone",
  "Status"
];

const validateDistributorFile = async (file: File): Promise<string[]> => {
  const errors: string[] = [];

  try {
    const data = await file.arrayBuffer();
    const workbook = XLSX.read(data);
    const sheet = workbook.Sheets[workbook.SheetNames[0]];

    const sheetData = XLSX.utils.sheet_to_json(sheet, {
      header: 1,
      defval: ""
    });

    if (!sheetData.length) {
      return ["Excel file is empty"];
    }

    const headerRow = sheetData[0] as any[];

    const actualHeaders = headerRow.map((h) =>
      String(h).trim().toUpperCase()
    );

    const expected = distributorExpectedHeaders.map((h) =>
      h.toUpperCase()
    );

    for (let i = 0; i < expected.length; i++) {
      if (actualHeaders[i] !== expected[i]) {
        errors.push(
          `Column ${i + 1}: expected "${distributorExpectedHeaders[i]}" but found "${headerRow[i] || ""}"`
        );
      }
    }

    if (sheetData.length < 2) {
      errors.push("File has no Distributor records.");
    }
  } catch {
    errors.push("Unable to read Excel file.");
  }

  return errors;
};



const handleDistributorUpload = async () => {
  if (!selectedFile) {
    setUploadErrors(["No file selected"]);
    return;
  }

  setUploadLoading(true);
  setUploadErrors([]);
  setUploadSuccess(null);

  const errors = await validateDistributorFile(selectedFile);

  if (errors.length) {
    setUploadErrors(errors);
    setUploadLoading(false);
    return;
  }

  try {
    const formData = new FormData();

    formData.append("file", selectedFile);
    formData.append("vendorId", vendorId);
    formData.append("franchiseId", franchiseId);

    formData.append("companyId", selectedCompanyId);
    formData.append("companyName", selectedCompanyName);
    formData.append("priceListId", priceListId);
    formData.append("billingTaxId", billingTaxId);
    formData.append("distributorId", selectedDistributorId);

    console.log('formdata',formData)

    if(selectedUploadType === "DISTRIBUTOR") {
      if (!selectedCompanyId) {
        showSnackbar("Please select Company", "error");
        return;
      }

      if (!priceListId) {
        showSnackbar("Please select Franchisee Price List", "error");
        return;
      }

      if (!billingTaxId) {
        showSnackbar("Please select Billing Tax Type", "error");
        return;
      }
    } else {
      if (!selectedDistributorId) {
        showSnackbar("Please select Distributor", "error");
        return;
      }
    }

    if (!selectedFile) {
      showSnackbar("Please select Excel file", "error");
      return;
    }

    let response = null;

    if(selectedUploadType === "DISTRIBUTOR") {
      response = await multy_post(`${API_URL}/api/import/distributor/upload`, formData);
    } else {
      response = await multy_post(`${API_URL}/api/import/distributor/upload`, formData);
    }

    const responseCode =
      response.responseCode ||
      response.statusCode ||
      response.status;

    if (
      responseCode === 200 ||
      responseCode === "200" ||
      responseCode === 201
    ) {
      showSnackbar(response.message, "success");
      setUploadSuccess(response.message);

      await getOperatorList();

      setTimeout(() => {
        setOpenBulkDistributorDialog(false);
        setSelectedFile(null);
        setUploadSuccess(null);
      }, 1500);
    } else {
      setUploadErrors([response.message]);
    }
  } catch (err: any) {
    setUploadErrors([
      err?.response?.data?.message || "Distributor upload failed"
    ]);
  } finally {
    setUploadLoading(false);
  }
};

const downloadDistributorSampleFile = () => {
  const sampleData = [
    distributorExpectedHeaders,
    [
      "India",
      "Telangana",
      "Hyderabad",
      "Hyderabad",
      "Kukatpally",
      "ABC Distributor",
      "abc@gmail.com",
      "password123",
      "0401234567",
      "9876543210",
      "Address 1",
      "",
      "",
      "500072",
      "Zone 1",
      "ACTIVE"
    ]
  ];

  const ws = XLSX.utils.aoa_to_sheet(sampleData);

  const wb = XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(
    wb,
    ws,
    "Distributor Bulk Upload"
  );

  XLSX.writeFile(
    wb,
    "distributor_bulk_upload_sample.xlsx"
  );
};
*/
const fetchCompanies = async () => {
  try {
    const response = await get(
      `${API_URL}/api/company/vendor/${vendorId}/fetchAll`
    );
    console.log('responsecom',response)
    if (response) {
      setCompaniesList(response);
    }
  } catch (error) {
    showSnackbar("Failed to load companies", "error");
  }
};

const fetchPriceListByVendor = async () => {
  try {
    const response = await get(
      `${API_URL}/api/area-wise-price-list/vendor/${vendorId}/getPriceListsByVendorId`
    );

    if (response?.data) {
      setPriceLists(response.data);
    }
  } catch (err) {
    console.error(err);
  }
};

const fetchBillingTaxTypes = async () => {
  try {
    const response = await get(
      `${API_URL}/api/billing-tax-type/vendor/${vendorId}/getAll`
    );

    if (response?.success) {
      setBillingTaxTypes(response.data);
    }
  } catch (err) {
    console.error(err);
  }
};

useEffect(() => {
  fetchCompanies();
  fetchPriceListByVendor();
  fetchBillingTaxTypes();
}, []);



  return (
    <>
      <Helmet>
        <title>Users</title>
      </Helmet>

      {rightsUserId ? (
        <Box sx={{}}>
          <ManageUserRights
            userName={rightsUserId}
            onClose={() => setRightsUserId(null)} onSave={function (data: any): void {
              throw new Error('Function not implemented.');
            }} />
        </Box>
      ) : editUserId ? (
        <Box sx={{}}>
          <EditOperator
            operatorId={editUserId}
            onClose={() => setEditUserId(null)}
            onSuccess={() => {
              alert('Operator Updated (Dummy Action)');
              setEditUserId(null);
            }}
          />
        </Box>
      ) : showAddForm ? (
        <Box sx={{}}>
          <AddOperator
            onClose={() => setShowAddForm(false)}
            onSuccess={() => {
              alert('Operator Added (Dummy Action)');
              setShowAddForm(false);
            }}
          />
        </Box>
      ) : (
        <>
          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
            <PageTitleWrapper>
              <PageTitle
                heading="Manage Operator"
                subHeading="Manage Operator and their access rights."
                handleClick={() => navigate('/Operator/addoperator')}
                label="Add"
              />
            </PageTitleWrapper>
          </Box>

          {/* ================= MOBILE UI ================= */}
          {isMobile ? (
            <Box sx={{ px: 1 }}>

              {/* STICKY HEADER */}
              <Box
                sx={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 10,
                  backgroundColor: theme.palette.background.default,
                  pt: 1,
                  pb: 1,
                  borderBottom: `1px solid ${theme.palette.divider}`
                }}
              >

                {/* HEADER ROW */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 1,
                    flexWrap: 'wrap',
                    gap: 1
                  }}
                >
                  <Typography sx={{ fontSize: '18px', fontWeight: 600 }}>
                    Manage Operator
                  </Typography>

                  <Box sx={{ display: 'flex', gap: 1 }}>
                    { accountType==="OWNER" && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setOpenBulkDialog(true)}
                        sx={{ minWidth: 'auto', px: 2, height: '32px' }}
                      >
                        Import LCO/Distributor
                      </Button>
                    )}
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => navigate('/Operator/addoperator')}
                      sx={{ minWidth: 'auto', px: 2, height: '32px' }}
                    >
                      + Add
                    </Button>
                  </Box>
                </Box>

                {/* SEARCH */}
                <TextField
                  label="Search"
                  variant="outlined"
                  value={searchQuery}
                  onChange={handleSearch}
                  fullWidth
                  size="small"
                />

              </Box>

              {/* SCROLLABLE CARDS */}
              <Box sx={{ mt: 1 }}>
                {filteredRows.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="textSecondary">No operators found</Typography>
                  </Box>
                ) : (
                  filteredRows.map((row) => (
                    <Box
                      key={row.id}
                      sx={{
                        borderRadius: '14px',
                        p: 2,
                        mb: 1.5,
                        background: 'linear-gradient(135deg, #0b1a3a, #132a5e)',
                        color: '#fff',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.4)'
                      }}
                    >
                      <Typography sx={{ fontWeight: 600 }}>
                        {row.operatorName}
                      </Typography>

                      <Typography sx={{ fontSize: '13px', opacity: 0.8 }}>
                        📞 {row.phone || 'N/A'}
                      </Typography>

                      <Typography sx={{ fontSize: '13px' }}>
                        📧 {row.email}
                      </Typography>

                      <Typography sx={{ fontSize: '13px', mt: 0.5 }}>
                        🔑 {row.accountType}
                      </Typography>

                      <Box
                        sx={{
                          mt: 1,
                          display: 'inline-block',
                          px: 1.2,
                          py: 0.4,
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: 600,
                          backgroundColor:
                            row.status === 'ACTIVE' ? '#2e7d32' : '#d32f2f'
                        }}
                      >
                        {row.status}
                      </Box>

                      <Box sx={{ mt: 1.5, display: 'flex', gap: 1 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          fullWidth
                          onClick={() =>
                            navigate(`/Operator/editoperator/${row.id}`)
                          }
                        >
                          Edit
                        </Button>

                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          fullWidth
                          onClick={() => {
                            setDeleteId(row.id);
                            setDeleteName(row.operatorName);
                            setOpenDeleteDialog(true);
                          }}
                        >
                          Delete
                        </Button>
                      </Box>
                    </Box>
                  ))
                )}
              </Box>

            </Box>
          ) : (
            /* ================= DESKTOP UI ================= */
            <>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mb: 2,
                  gap: 2,
                  padding: '0 5px',
                  flexWrap: 'wrap'
                }}
              >
                <TextField
                  label="Search"
                  variant="outlined"
                  value={searchQuery}
                  onChange={handleSearch}
                  sx={{ width: '25%', minWidth: '200px' }}
                  size="small"
                />

                 <FormControl size="small" sx={{ minWidth: 180 }}>
    <InputLabel>Account Type</InputLabel>
    <Select
      value={accountTypeFilter}
      label="Account Type"
      onChange={(e) => setAccountTypeFilter(e.target.value)}
    >
      <MenuItem value="">All</MenuItem>
      <MenuItem value="DISTRIBUTOR">DISTRIBUTOR</MenuItem>
      <MenuItem value="LCO">LCO</MenuItem>
    </Select>
  </FormControl>

                  { accountType==="OWNER" && (
                    <Button
                      variant="contained"
                      color="success"
                      onClick={() => setOpenBulkDialog(true)}
                      sx={{ height: 40 }}
                      startIcon={<CloudUploadIcon />}
                    >
                      Import LCO/Distributor
                    </Button>
                  )}
              </Box>

              <Box sx={{ height: 'calc(100vh - 250px)', padding: '15px' }}>
                <DataGrid
                  rows={filteredRows || []}
                  columns={columns}
                  loading={loading}
                  getRowId={(row) => row.id}
                  components={{ Toolbar: GridToolbar }}
                  pageSize={10}
                  rowsPerPageOptions={[10, 25, 50]}
                  disableSelectionOnClick
                  sx={{
                    '& .MuiDataGrid-cell': {
                      fontSize: '14px'
                    }
                  }}
                />
              </Box>
            </>
          )}

          {/* Bulk Upload Dialog */}
          {/* <Dialog
            open={openBulkDialog}
            onClose={() => {
              if (!uploadLoading) {
                setOpenBulkDialog(false);
                setSelectedFile(null);
                setUploadErrors([]);
                setUploadSuccess(null);
              }
            }}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>
              <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
                Bulk Upload LCOs
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                Upload Excel file to create multiple LCOs at once
              </Typography>
            </DialogTitle>

            <DialogContent>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Distributor *</InputLabel>

                      <Select
                        value={selectedDistributorId}
                        label="Distributor *"
                        onChange={(e) => {
                          const id = e.target.value;

                          const d = distributors.find((c: any) => c.id === id);

                          setSelectedDistributorId(id);
                        }}
                      >
                  <MenuItem value="">Select Distributor</MenuItem>

                  {distributors.map((dist: any) => (
                    <MenuItem key={dist.id} value={dist.id}>
                      {dist?.operatorName}
                    </MenuItem>
                  ))}
                </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              {/* Drag & Drop Area *}
              <Box
                sx={{
                  border: "2px dashed #aaa",
                  padding: "30px",
                  textAlign: "center",
                  cursor: "pointer",
                  borderRadius: 2,
                  backgroundColor: selectedFile ? '#f0f8f0' : 'transparent',
                  transition: 'all 0.3s',
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: '#f5f5f5'
                  }
                }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  const file = e.dataTransfer.files[0];
                  handleFile(file);
                }}
                onClick={() => document.getElementById("fileInput")?.click()}
              >
                {selectedFile ? (
                  <Box>
                    <Typography variant="h6" color="primary">
                      ✓ {selectedFile.name}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Click or drag to change file
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {(selectedFile.size / 1024).toFixed(2)} KB
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <CloudUploadIcon sx={{ fontSize: 48, color: '#aaa', mb: 1 }} />
                    <Typography variant="h6">Drag & Drop Excel File</Typography>
                    <Typography variant="body2" color="textSecondary">
                      OR Click to Browse
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Supported formats: .xlsx, .xls, .csv (Max 5MB)
                    </Typography>
                  </>
                )}
                <input
                  id="fileInput"
                  type="file"
                  hidden
                  accept=".xlsx,.xls,.csv"
                  onChange={(e) => {
                    if (e.target.files && e.target.files.length > 0) {
                      handleFile(e.target.files[0]);
                    }
                  }}
                />
              </Box>

              {/* Success Message *}
              {uploadSuccess && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  {uploadSuccess}
                </Alert>
              )}

              {/* Error Display *}
              {uploadErrors.length > 0 && (
                <Box sx={{ mt: 2, maxHeight: 200, overflow: 'auto' }}>
                  {uploadErrors.map((err, i) => (
                    <Alert key={i} severity="error" sx={{ mb: 1 }}>
                      {err}
                    </Alert>
                  ))}
                </Box>
              )}

              {/* Download Sample *}
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  startIcon={<DownloadIcon />}
                  onClick={downloadSampleFile}
                  variant="outlined"
                  size="small"
                >
                  Download Sample File
                </Button>
              </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button
                onClick={() => {
                  setOpenBulkDialog(false);
                  setSelectedFile(null);
                  setUploadErrors([]);
                  setUploadSuccess(null);
                }}
                disabled={uploadLoading}
              >
                Cancel
              </Button>

              <Button
                variant="contained"
                color="primary"
                disabled={!selectedFile || uploadLoading}
                onClick={handleUpload}
                startIcon={uploadLoading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
              >
                {uploadLoading ? "Importing..." : "Import LCOs"}
              </Button>
            </DialogActions>
          </Dialog> */}

          {/* Bulk Upload Distributor*/}
          <Dialog
            open={openBulkDialog}
            onClose={() => {
              if (!uploadLoading) {
                setOpenBulkDialog(false);
                setSelectedFile(null);
                setUploadErrors([]);
                setUploadSuccess(null);
              }
            }}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>
              <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
                Import LCO/Distributors
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                Upload Excel file to create multiple Distributors at once
              </Typography>
            </DialogTitle>

            <DialogContent>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Upload Type *</InputLabel>

                    <Select
                      value={selectedUploadType}
                      label="Upload Type *"
                      onChange={(e) => handleUploadFieldsChange(e.target.value)}
                    >
                      <MenuItem value="LCO">LCO</MenuItem>
                      <MenuItem value="DISTRIBUTOR">DISTRIBUTOR</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {/* LCO Fields */}
              {uploadFields.showLcoFields && (
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Distributor *</InputLabel>

                      <Select
                        value={selectedDistributorId}
                        label="Distributor *"
                        onChange={(e) => {
                          const id = e.target.value;

                          const d = distributors.find((c: any) => c.id === id);

                          setSelectedDistributorId(id);
                        }}
                      >
                  <MenuItem value="">Select Distributor</MenuItem>

                  {distributors.map((dist: any) => (
                    <MenuItem key={dist.id} value={dist.id}>
                      {dist?.operatorName}
                    </MenuItem>
                  ))}
                </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              )}

              {/* Distributor Fields */}
              {uploadFields.showDistributorFields && (
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Company *</InputLabel>

                      <Select
                        value={selectedCompanyId}
                        label="Company *"
                        onChange={(e) => {
                          const id = e.target.value;

                          const company = companiesList.find((c: any) => c.id === id);

                          setSelectedCompanyId(id);
                          setSelectedCompanyName(company?.companyName || "");
                        }}
                      >
                        <MenuItem value="">Select Company</MenuItem>

                        {companiesList.map((company: any) => (
                          <MenuItem key={company.id} value={company.id}>
                            {company.companyName}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Area Price List *</InputLabel>
                      <Select
                        value={priceListId}
                        label="Area Price List *"
                        onChange={(e) => setPriceListId(e.target.value)}
                      >
                        <MenuItem value="">Select Price List</MenuItem>

                        {priceLists.map((item: any) => (
                          <MenuItem key={item.id} value={item.id}>
                            {item.areaName} - {item.locationName}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Billing Tax Type</InputLabel>

                      <Select
                        value={billingTaxId}
                        label="Billing Tax Type"
                        onChange={(e) => setBillingTaxId(e.target.value)}
                      >
                        <MenuItem value="">Select Billing Tax</MenuItem>

                        {billingTaxTypes.map((item: any) => (
                          <MenuItem key={item.id} value={item.id}>
                            {item.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              )}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">
                      Fields
                    </Typography>

                    <Box sx={{ m: 1 }}>
                      <Typography sx={{ color: "red" }}>
                        {expectedHeaders.join(", ")}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
              {/* Drag & Drop Area */}
              <Box
                sx={{
                  border: "2px dashed #aaa",
                  padding: "30px",
                  textAlign: "center",
                  cursor: "pointer",
                  borderRadius: 2,
                  backgroundColor: selectedFile ? '#313131' : 'transparent',
                  transition: 'all 0.3s',
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: '#313131'
                  }
                }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  const file = e.dataTransfer.files[0];
                  handleFile(file);
                }}
                onClick={() => document.getElementById("fileInput")?.click()}
              >
                {selectedFile ? (
                  <Box>
                    <Typography variant="h6" color="primary">
                      ✓ {selectedFile.name}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Click or drag to change file
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {(selectedFile.size / 1024).toFixed(2)} KB
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <CloudUploadIcon sx={{ fontSize: 48, color: '#aaa', mb: 1 }} />
                    <Typography variant="h6">Drag & Drop Excel File</Typography>
                    <Typography variant="body2" color="textSecondary">
                      OR Click to Browse
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Supported formats: .xlsx, .xls, .csv (Max 5MB)
                    </Typography>
                  </>
                )}
                <input
                  id="fileInput"
                  type="file"
                  hidden
                  accept=".xlsx,.xls,.csv"
                  onChange={(e) => {
                    if (e.target.files && e.target.files.length > 0) {
                      handleFile(e.target.files[0]);
                    }
                  }}
                />
              </Box>

              {/* Success Message */}
              {uploadSuccess && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  {uploadSuccess}
                </Alert>
              )}

              {/* Error Display */}
              {uploadErrors.length > 0 && (
                <Box sx={{ mt: 2, maxHeight: 200, overflow: 'auto' }}>
                  {uploadErrors.map((err, i) => (
                    <Alert key={i} severity="error" sx={{ mb: 1 }}>
                      {err}
                    </Alert>
                  ))}
                </Box>
              )}

              {/* Download Sample */}
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  startIcon={<DownloadIcon />}
                  onClick={downloadSampleFile}
                  variant="outlined"
                  size="small"
                >
                  Download Sample File
                </Button>
              </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button
                onClick={() => {
                  setOpenBulkDialog(false);
                  setSelectedFile(null);
                  setUploadErrors([]);
                  setUploadSuccess(null);
                }}
                disabled={uploadLoading}
              >
                Cancel
              </Button>

              <Button
  variant="contained"
  color="primary"
  disabled={
    !selectedFile ||
    uploadLoading
  }
  onClick={handleUpload}
  startIcon={
    uploadLoading ? <CircularProgress size={20} /> : <CloudUploadIcon />
  }
>
  {uploadLoading ? "Importing..." : "Import LCO/Distributors"}
</Button>
            </DialogActions>
          </Dialog>

          <ConfirmDeleteDialog
            open={openDeleteDialog}
            onClose={() => setOpenDeleteDialog(false)}
            onConfirm={deleteOperator}
            itemName={deleteName}
            context="Are you sure you want to delete this Operator"
          />
        </>
      )}
    </>
  );
};

export default OperatorScreen;